import React, { useState } from "react";

type Props = {
    message: string;
    className?: string;           // extra classes for the wrapper
    maxWidth?: number;            // px (default 360)
    children?: React.ReactNode;   // the trigger (icon/button/etc.)
    position?: "top" | "right" | "bottom" | "left"; // tooltip position
    fontSize?: number;            // font size in px (default 12.5)
    backgroundColor?: string;     // background color (default #2563eb)
};

export default function TooltipBubble({
    message,
    className,
    maxWidth = 360,
    children,
    position = "top",
    fontSize = 12.5,
    backgroundColor = "#2563eb",
}: Props) {
    const [open, setOpen] = useState(false);

    // Position styles for tooltip
    const positionStyles: Record<string, React.CSSProperties> = {
        top: {
            left: "50%",
            bottom: "100%",
            transform: "translateX(-50%)",
            marginBottom: "8px",
        },
        right: {
            left: "100%",
            top: "50%",
            transform: "translateY(-50%)",
            marginLeft: "8px",
        },
        bottom: {
            left: "50%",
            top: "100%",
            transform: "translateX(-50%)",
            marginTop: "8px",
        },
        left: {
            right: "100%",
            top: "50%",
            transform: "translateY(-50%)",
            marginRight: "8px",
        },
    };

    // Pointer position styles
    const pointerStyles: Record<string, React.CSSProperties> = {
        top: {
            left: "50%",
            bottom: -6,
            transform: "translateX(-50%) rotate(45deg)",
        },
        right: {
            left: -6,
            top: "50%",
            transform: "translateY(-50%) rotate(45deg)",
        },
        bottom: {
            left: "50%",
            top: -6,
            transform: "translateX(-50%) rotate(45deg)",
        },
        left: {
            right: -6,
            top: "50%",
            transform: "translateY(-50%) rotate(45deg)",
        },
    };

    return (
        <span
            className={`tt-wrapper d-inline-block ${className || ""}`}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onClick={() => setOpen((p) => !p)} // tap-friendly for mobile
            role="button"
            tabIndex={0}
            aria-label="More info"
            style={{ position: "relative", display: "inline-block" }}
        >
            {children ?? (
                <i className="bi bi-info-circle ms-1" aria-hidden="true" />
            )}

            {open && (
                <div
                    className="tt-bubble"
                    role="tooltip"
                    style={{
                        position: "absolute",
                        ...positionStyles[position],
                        background: backgroundColor,
                        color: "#fff",
                        padding: "10px 14px",
                        borderRadius: 10,
                        boxShadow: "0 8px 24px rgba(0,0,0,.16)",
                        fontSize,
                        lineHeight: 1.35,
                        fontWeight: 500,
                        maxWidth,
                        width: "max-content",
                        zIndex: 1000,
                        textAlign: "left",
                        whiteSpace: "normal",
                    }}
                >
                    {message}

                    {/* pointer */}
                    <span
                        style={{
                            position: "absolute",
                            ...pointerStyles[position],
                            width: 12,
                            height: 12,
                            background: backgroundColor,
                            borderRadius: 2,
                        }}
                    />
                </div>
            )}
            <style>{`
                @media (max-width: 576px) {
                    .tt-bubble { 
                        max-width: calc(100vw - 40px) !important;
                        font-size: 13px !important;
                    }
                }
            `}</style>
        </span>
    );
}
